/**
 * Theatre API Module
 *
 * Wire to backend theatre endpoints:
 *   POST   /api/v1/theatres              — create theatre
 *   GET    /api/v1/theatres/:id          — get theatre
 *   POST   /api/v1/theatres/:id/commit   — commit theatre
 *   POST   /api/v1/theatres/:id/run      — run theatre
 *   POST   /api/v1/theatres/:id/settle   — settle theatre
 *   GET    /api/v1/theatres/:id/commitment — get commitment receipt
 *   GET    /api/v1/theatres/:id/certificate — get theatre certificate
 *   GET    /api/v1/theatres/:id/replay   — get replay
 *   GET    /api/v1/templates             — list templates
 *   GET    /api/v1/templates/:id         — get template
 *   GET    /api/v1/certificates          — list certificates
 *   GET    /api/v1/certificates/:id      — get certificate
 *   GET    /api/v1/theatres/             — list theatres (theatre_routes.py:305)
 */

import { apiClient } from './client';
import type {
  TheatreResponse,
  TheatreListResponse,
  TheatreCreateRequest,
  CommitmentReceiptResponse,
  TheatreCertificateResponse,
  TemplateDetailResponse,
  TemplateListResponse,
  CertificateListResponse,
  ActivationReadinessResponse,
  ActivationResponse,
  RuntimeReadinessResponse,
  RuntimeActivationResponse,
  DatasetBindingReadinessResponse,
  DatasetBindingResult,
} from '../types/theatre';
import type { CanvasNodeState, CanvasConnection } from '../components/environment/types';

/** Shape of persisted build state from backend. */
export interface BuildStatePayload {
  nodes: Record<string, CanvasNodeState>;
  connections: CanvasConnection[];
}

// ── Theatre CRUD ────────────────────────────────────────────────────────

export const theatreApi = {
  /** Create a new theatre in DRAFT state */
  createTheatre: async (req: TheatreCreateRequest): Promise<TheatreResponse> => {
    const { data } = await apiClient.post('/api/v1/theatres/', req);
    return data;
  },

  /** List theatres visible to the current user (GET /api/v1/theatres/) */
  listTheatres: async (params?: {
    state?: string;
    limit?: number;
    offset?: number;
  }): Promise<TheatreListResponse> => {
    const { data } = await apiClient.get('/api/v1/theatres/', { params });
    return data;
  },

  /** Get a single theatre by ID */
  getTheatre: async (
    theatreId: string,
    options?: { includeInvestigationContext?: boolean },
  ): Promise<TheatreResponse> => {
    const params = options?.includeInvestigationContext
      ? { include: 'investigation_context' }
      : undefined;
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}`, { params });
    return data;
  },

  /** Commit a DRAFT theatre — locks parameters, generates commitment hash */
  commitTheatre: async (theatreId: string): Promise<CommitmentReceiptResponse> => {
    const { data } = await apiClient.post(`/api/v1/theatres/${theatreId}/commit`);
    return data;
  },

  /** Start running a COMMITTED theatre */
  runTheatre: async (theatreId: string): Promise<TheatreResponse> => {
    const { data } = await apiClient.post(`/api/v1/theatres/${theatreId}/run`);
    return data;
  },

  /** Settle an ACTIVE theatre — triggers resolution and certificate generation */
  settleTheatre: async (theatreId: string): Promise<TheatreResponse> => {
    const { data } = await apiClient.post(`/api/v1/theatres/${theatreId}/settle`, {
      settlement_data: {},
    });
    return data;
  },

  /** Get commitment receipt for a committed theatre */
  getCommitment: async (theatreId: string): Promise<CommitmentReceiptResponse> => {
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}/commitment`);
    return data;
  },

  /** Get calibration certificate for a resolved theatre */
  getTheatreCertificate: async (theatreId: string): Promise<TheatreCertificateResponse> => {
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}/certificate`);
    return data;
  },

  /** Get fork replay for a theatre */
  getReplay: async (theatreId: string): Promise<unknown> => {
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}/replay`);
    return data;
  },

  /** Get persisted Environment Build canvas state */
  getBuildState: async (theatreId: string): Promise<BuildStatePayload | null> => {
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}/build-state`);
    return data.build_state ?? null;
  },

  /** Save Environment Build canvas state */
  saveBuildState: async (
    theatreId: string,
    state: BuildStatePayload,
  ): Promise<BuildStatePayload> => {
    const { data } = await apiClient.put(`/api/v1/theatres/${theatreId}/build-state`, state);
    return data.build_state;
  },

  // ── FORGE Activation Bridge ────────────────────────────────────────

  /** Check activation readiness (MATERIALIZED → COMMITTED) */
  getActivationReadiness: async (theatreId: string): Promise<ActivationReadinessResponse> => {
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}/activation-readiness`);
    return data;
  },

  /** Activate a MATERIALIZED theatre → COMMITTED */
  activateTheatre: async (theatreId: string): Promise<ActivationResponse> => {
    const { data } = await apiClient.post(`/api/v1/theatres/${theatreId}/activate`);
    return data;
  },

  // ── Runtime Bridge ─────────────────────────────────────────────────

  /** Check runtime readiness (COMMITTED → ACTIVE) */
  getRuntimeReadiness: async (theatreId: string): Promise<RuntimeReadinessResponse> => {
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}/runtime-readiness`);
    return data;
  },

  /** Start runtime: COMMITTED → ACTIVE */
  startRuntime: async (theatreId: string): Promise<RuntimeActivationResponse> => {
    const { data } = await apiClient.post(`/api/v1/theatres/${theatreId}/start-runtime`);
    return data;
  },

  // ── Dataset Binding ────────────────────────────────────────────────

  /** Check dataset binding readiness (COMMITTED + FORGE) */
  getDatasetBindingReadiness: async (theatreId: string): Promise<DatasetBindingReadinessResponse> => {
    const { data } = await apiClient.get(`/api/v1/theatres/${theatreId}/dataset-binding-readiness`);
    return data;
  },

  /** Bind FORGE source-reference datasets to a COMMITTED theatre */
  bindDatasets: async (theatreId: string): Promise<DatasetBindingResult> => {
    const { data } = await apiClient.post(`/api/v1/theatres/${theatreId}/bind-datasets`);
    return data;
  },
};

// ── Templates ───────────────────────────────────────────────────────────

export const templateApi = {
  /** List available theatre templates */
  listTemplates: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<TemplateListResponse> => {
    const { data } = await apiClient.get('/api/v1/templates/', { params });
    return data;
  },

  /** Get a single template by ID */
  getTemplate: async (templateId: string): Promise<TemplateDetailResponse> => {
    const { data } = await apiClient.get(`/api/v1/templates/${templateId}`);
    return data;
  },
};

// ── Theatre Certificates (list) ─────────────────────────────────────────

export const theatreCertificateApi = {
  /** List all theatre certificates */
  listCertificates: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<CertificateListResponse> => {
    const { data } = await apiClient.get('/api/v1/certificates/', { params });
    return data;
  },

  /** Get a single certificate by ID */
  getCertificate: async (certificateId: string): Promise<TheatreCertificateResponse> => {
    const { data } = await apiClient.get(`/api/v1/certificates/${certificateId}`);
    return data;
  },
};
